import { useQueryClient } from '@tanstack/react-query';
import { isNil } from 'lodash';
import { useState, useCallback } from 'react';
import { DataType } from 'src/components/formEditor/uiEditor/uiEditor';
import { ProcessAnswerFuncType } from 'src/features/generalForms/fillingModal/GeneralFormFillingModal.t';
import { useGeneralFormFillingModal } from 'src/features/generalForms/fillingModal/useGeneralFormFillingModal';
import { useStudy } from 'src/helpers/hooks/useStudy';
import {
  IScoreResult,
  StudyNoteProcessingDto,
  VersionType,
  ScoreResult,
  IStudyNoteResultDto,
  FileParameter,
  IStudyNoteConfigDto,
} from 'src/services/api/api-client';
import {
  processNewStudyNote,
  processNewStudyNoteVersion,
  uploadResultFile,
} from 'src/services/api/api-client/StudyNotesClient';
import { getStudyNotesQueryKey } from 'src/services/api/api-client/StudyNotesQuery';

export const useStudyNoteFillingModal = () => {
  const queryClient = useQueryClient();
  const study = useStudy();
  const [resultId, setResultId] = useState<number>();

  const processAnswerHandler = useCallback(
    async (data: DataType, scoreResults: IScoreResult[], filesToCommit: string[]) => {
      const fieldSkipReasons = null;
      await processNewStudyNote(
        study!.id,
        new StudyNoteProcessingDto({
          value: data,
          fieldSkipReasons: fieldSkipReasons,
          versionType: VersionType.Major,
          scoreResults: scoreResults.map((x) => new ScoreResult(x)),
          fileIdsToCommit: filesToCommit,
        }),
      );
    },
    [study],
  );

  const patchAnswerHandler = useCallback(
    async (id: number, data: DataType, scoreResults: IScoreResult[], filesToCommit: string[]) => {
      const fieldSkipReasons = null;
      await processNewStudyNoteVersion(
        study!.id,
        id,
        new StudyNoteProcessingDto({
          value: data,
          fieldSkipReasons: fieldSkipReasons,
          versionType: VersionType.Major,
          scoreResults: scoreResults.map((x) => new ScoreResult(x)),
          fileIdsToCommit: filesToCommit,
        }),
      );
    },
    [study],
  );

  const processAnswerFunc: ProcessAnswerFuncType = useCallback(
    async (data, scoreResults, fileIdsToCommit) => {
      if (isNil(resultId)) await processAnswerHandler(data, scoreResults, fileIdsToCommit);
      else await patchAnswerHandler(resultId!, data, scoreResults, fileIdsToCommit);

      await queryClient.invalidateQueries(getStudyNotesQueryKey(study!.id));
    },
    [patchAnswerHandler, processAnswerHandler, queryClient, resultId, study],
  );

  const uploadFileFunc = useCallback(
    async (file: FileParameter) => {
      return uploadResultFile(study!.id, file);
    },
    [study],
  );

  const { modal, open } = useGeneralFormFillingModal({
    processAnswerFunc,
    uploadFileFunc,
  });

  const openHandler = useCallback(
    async (result: IStudyNoteResultDto | null, config: IStudyNoteConfigDto) => {
      setResultId(result?.formResultId);
      open({
        layout: config.layout,
        scoreSections: config.scoreSections ?? [],
        variables: config.variables ?? [],
        initialValue: result?.value,
        resultId: result?.formResultId,
        modalTitle: config.title,
      });
    },
    [open],
  );

  return {
    modal,
    openHandler,
  };
};
